/**
 * /admissions — funnel home (candidates / accepted / new interview).
 *
 * Replaces the pre-funnel form that assumed students already existed.
 * Now serves as the entry to the whole admissions pipeline:
 *
 *   candidate+interviewed → click → /admissions/interview/{id}
 *   accepted              → click → AddStudentsModal pre-targeted
 *   + new interview       → ApplicantIntakeModal
 *
 * The escape-hatch "direct score entry" lives on the interview page
 * itself, not here — it's a per-interview decision.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useAccepted,
  useCandidates,
  useDecideAdmission,
  useSchool,
  useStartInterview,
  type Candidate,
} from "../../api/school";
import { ApplicantIntakeModal } from "./ApplicantIntakeModal";
import { PlaceCandidateModal } from "./PlaceCandidateModal";

type Tab = "candidates" | "accepted";

export function AdmissionPage() {
  const { t } = useTranslation();
  const school = useSchool();
  const candidates = useCandidates(school.data?.id);
  const accepted = useAccepted(school.data?.id);

  const [tab, setTab] = useState<Tab>("candidates");
  const [showIntake, setShowIntake] = useState(false);
  const [placing, setPlacing] = useState<Candidate | null>(null);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">{t("admissions.title")}</h1>
          <p className="text-xs text-slate-500 mt-1">
            {t("admissions.subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowIntake(true)}
          className="px-3 py-1.5 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 whitespace-nowrap"
        >
          {t("admissions.newInterview")}
        </button>
      </header>

      <div className="flex gap-1 border-b border-slate-200 mb-4 -mb-px">
        <TabBtn active={tab === "candidates"} onClick={() => setTab("candidates")}>
          {t("admissions.tabs.candidates")}
          <Count n={candidates.data?.length} />
        </TabBtn>
        <TabBtn active={tab === "accepted"} onClick={() => setTab("accepted")}>
          {t("admissions.tabs.accepted")}
          <Count n={accepted.data?.length} />
        </TabBtn>
      </div>

      {tab === "candidates" ? (
        <CandidatesPool
          data={candidates.data}
          isLoading={candidates.isLoading}
        />
      ) : (
        <AcceptedPool
          data={accepted.data}
          isLoading={accepted.isLoading}
          onPlace={(c) => setPlacing(c)}
        />
      )}

      {showIntake && school.data && (
        <ApplicantIntakeModal
          schoolId={school.data.id}
          onClose={() => setShowIntake(false)}
        />
      )}
      {placing && school.data && (
        <PlaceCandidateModal
          candidate={placing}
          schoolId={school.data.id}
          onClose={() => setPlacing(null)}
        />
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm border-b-2 -mb-px transition flex items-center gap-2 ${
        active
          ? "border-sky-500 text-sky-600 font-medium"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function Count({ n }: { n: number | undefined }) {
  if (n === undefined) return null;
  return (
    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-slate-100 text-slate-600 text-xs font-mono">
      {n}
    </span>
  );
}

// ── Candidates pool ────────────────────────────────────────────────

function CandidatesPool({
  data,
  isLoading,
}: {
  data: Candidate[] | undefined;
  isLoading: boolean;
}) {
  const { t } = useTranslation();
  const startInterview = useStartInterview();

  // Split for sub-filter clarity: not-yet-interviewed vs interviewed
  // (awaiting decision). Both still under the parent Candidates tab —
  // same workflow, just different next-step CTAs.
  const split = useMemo(() => {
    const not_yet: Candidate[] = [];
    const done: Candidate[] = [];
    for (const c of data ?? []) {
      if (c.status === "interviewed") done.push(c);
      else not_yet.push(c);
    }
    return { not_yet, done };
  }, [data]);

  if (isLoading) return <p className="text-sm text-slate-500 italic">{t("common.loading")}</p>;
  if ((data?.length ?? 0) === 0)
    return (
      <div className="rounded border border-slate-200 bg-white p-8 text-center text-slate-500 text-sm">
        {t("admissions.candidates.noCandidates")}
      </div>
    );

  return (
    <div className="space-y-6">
      {split.not_yet.length > 0 && (
        <section>
          <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
            {t("admissions.candidates.notInterviewed", { count: split.not_yet.length })}
          </h2>
          <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
            {split.not_yet.map((c) => (
              <CandidateRow
                key={c.id}
                c={c}
                cta={
                  <button
                    type="button"
                    disabled={startInterview.isPending}
                    onClick={async () => {
                      const state = await startInterview.mutateAsync({
                        student_id: c.id,
                      });
                      window.location.href = `/admissions/interview/${state.admission_id}`;
                    }}
                    className="px-3 py-1 text-xs rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 whitespace-nowrap"
                  >
                    {t("admissions.candidates.startInterview")}
                  </button>
                }
              />
            ))}
          </ul>
        </section>
      )}
      {split.done.length > 0 && (
        <section>
          <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
            {t("admissions.candidates.interviewedPendingDecision", { count: split.done.length })}
          </h2>
          <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
            {split.done.map((c) => (
              <CandidateRow
                key={c.id}
                c={c}
                cta={<ReviewActions candidate={c} />}
              />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/** Per-row decision actions on the interviewed-pending-decision list.
 *  Accept moves the row into Awaiting placement; Reject closes the
 *  funnel (status='rejected'); View opens the read-only transcript
 *  for review before deciding. */
function ReviewActions({ candidate }: { candidate: Candidate }) {
  const { t } = useTranslation();
  const decide = useDecideAdmission(candidate.latest_admission_id ?? undefined);

  async function action(decision: "accept" | "reject") {
    if (!candidate.latest_admission_id) return;
    if (decision === "reject") {
      const ok = window.confirm(
        t("admissions.candidates.rejectConfirm", { name: candidate.name_zh }),
      );
      if (!ok) return;
    }
    await decide.mutateAsync({ decision });
  }

  return (
    <div className="flex w-full justify-end gap-1.5 whitespace-nowrap sm:w-auto">
      {candidate.latest_admission_id && (
        <Link
          to={`/admissions/interview/${candidate.latest_admission_id}`}
          className="px-2.5 py-1 text-xs rounded bg-slate-100 text-slate-700 hover:bg-slate-200"
        >
          {t("admissions.candidates.viewInterview")}
        </Link>
      )}
      <button
        type="button"
        onClick={() => action("accept")}
        disabled={decide.isPending}
        className="px-2.5 py-1 text-xs rounded bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50"
      >
        {t("admissions.candidates.accept")}
      </button>
      <button
        type="button"
        onClick={() => action("reject")}
        disabled={decide.isPending}
        className="px-2.5 py-1 text-xs rounded bg-rose-500 text-white hover:bg-rose-600 disabled:opacity-50"
      >
        {t("admissions.candidates.reject")}
      </button>
    </div>
  );
}

function CandidateRow({ c, cta }: { c: Candidate; cta: React.ReactNode }) {
  const { t } = useTranslation();
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-slate-50">
      <Link
        to={`/students/${c.id}`}
        className="flex-1 text-sm text-slate-800 hover:text-sky-700"
      >
        <span className="font-medium">{c.name_zh}</span>
        {c.name_en && (
          <span className="text-slate-500 text-xs ml-1">({c.name_en})</span>
        )}
        <span className="text-xs text-slate-400 ml-3 font-mono">
          {c.grade}
          {c.section ?? ""}
        </span>
      </Link>
      <div className="text-xs text-slate-500 w-32 text-right">
        {c.latest_interview_date
          ? t("admissions.candidates.interviewDate", { date: c.latest_interview_date })
          : t("admissions.candidates.notInterviewedYet")}
      </div>
      <div className="text-xs font-mono text-slate-700 w-12 text-right">
        {c.latest_overall_score ?? "—"}
      </div>
      {cta}
    </li>
  );
}

// ── Accepted pool ──────────────────────────────────────────────────

function AcceptedPool({
  data,
  isLoading,
  onPlace,
}: {
  data: Candidate[] | undefined;
  isLoading: boolean;
  onPlace: (c: Candidate) => void;
}) {
  const { t } = useTranslation();
  if (isLoading) return <p className="text-sm text-slate-500 italic">{t("common.loading")}</p>;
  if ((data?.length ?? 0) === 0)
    return (
      <div className="rounded border border-slate-200 bg-white p-8 text-center text-slate-500 text-sm">
        {t("admissions.accepted.noAccepted")}
      </div>
    );

  return (
    <ul className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
      {data!.map((c) => (
        <li
          key={c.id}
          className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50"
        >
          <Link
            to={`/students/${c.id}`}
            className="flex-1 text-sm text-slate-800 hover:text-sky-700"
          >
            <span className="font-medium">{c.name_zh}</span>
            {c.name_en && (
              <span className="text-slate-500 text-xs ml-1">({c.name_en})</span>
            )}
            <span className="text-xs text-slate-400 ml-3 font-mono">
              {c.grade}
              {c.section ?? ""}
            </span>
          </Link>
          <div className="text-xs font-mono text-slate-700 w-12 text-right">
            {c.latest_overall_score ?? "—"}
          </div>
          <button
            type="button"
            onClick={() => onPlace(c)}
            className="px-3 py-1 text-xs rounded bg-emerald-500 text-white hover:bg-emerald-600 whitespace-nowrap"
          >
            {t("admissions.accepted.assignClass")}
          </button>
        </li>
      ))}
    </ul>
  );
}
